import React, { useCallback, useMemo } from "react"
import { Box, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material"
import { useNavigate } from "react-router-dom"
import {
  CurrentEventsList,
  UpcomingEventsSlide,
} from "components/domains/events"
import { Carousel, CarouselItem } from "components/Carousel"
import { Head } from "../../components/Head"
import { EmptyData } from "components/EmptyData"
import { CalendarStarIcon } from "../../components/icons"
import { VerticalEventCard } from "../../components/domains/events"
import { VenueSelector } from "components/VenueSelector"

import { useTranslation } from "providers/i18n"
import { useEvent } from "hooks/event/useEvent"
import { getIcon } from "utils/icon"

export const Events = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const {
    isMultiVenueProject,
    venueFilter,
    venues,
    locationVenueId,
    groupEvents,
    activeGroupEvent,
    handleChange,
    changeVenueFilter,
  } = useEvent()

  const theme = useTheme()
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.events) || <CalendarStarIcon />,
    [theme]
  )
  const { styleOverrides } = theme.components?.[`EventPage`] || {}
  const {
    root,
    carouselContainer,
    carousel,
    eventGroupSelectorButton,
    cardListContainer,
    emptyDataWrapper,
    venueSelector,
  } = styleOverrides

  const handleClickSlide = useCallback(
    (_splide, { slide }, _event) => {
      navigate(slide.getAttribute("link"))
    },
    [navigate]
  )

  return (
    <>
      <Head title={t("Events & Activities")} />
      <Box sx={root}>
        <Box sx={cardListContainer}>
          <CurrentEventsList events={groupEvents["current"]} />
          <UpcomingEventsSlide events={groupEvents["upcoming"]} />
        </Box>
        <Box sx={carouselContainer}>
          {groupEvents[activeGroupEvent] &&
          groupEvents[activeGroupEvent].length > 0 ? (
            <Carousel
              onClick={handleClickSlide}
              options={{
                type: "loop",
                pagination: true,
                focus: "center",
                autoplay: true,
                gap: "0",
              }}
              sx={carousel}
            >
              {groupEvents[activeGroupEvent].map((event) => (
                <CarouselItem link={`/events/${event.id}`} key={event.id}>
                  <VerticalEventCard dense={true} event={event} />
                </CarouselItem>
              ))}
            </Carousel>
          ) : (
            <Box sx={emptyDataWrapper}>
              <EmptyData
                icon={emptyIcon}
                primary={
                  activeGroupEvent === "current"
                    ? t(`No Current Events`)
                    : t(`No Upcoming Events`)
                }
                secondary={t("Check Back Soon")}
              />
            </Box>
          )}
          <Box sx={eventGroupSelectorButton}>
            <ToggleButtonGroup
              value={activeGroupEvent}
              onChange={handleChange}
              exclusive
            >
              <ToggleButton value="current">{t("Current Events")}</ToggleButton>
              <ToggleButton value="upcoming">
                {t("Upcoming Events")}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
        {isMultiVenueProject && (
          <Box sx={venueSelector}>
            <VenueSelector
              selectedVenue={venueFilter}
              venues={venues}
              onClickVenue={changeVenueFilter}
              currentVenue={locationVenueId}
            />
          </Box>
        )}
      </Box>
    </>
  )
}
